import { useState, useEffect } from 'react';

// Breakpoint definitions following mobile-first approach
export const breakpoints = {
  mobile: 768,
  tablet: 1024,
  desktop: 1200,
  large: 1400
} as const;

export type Breakpoint = 'mobile' | 'tablet' | 'desktop' | 'large';

interface BreakpointHook {
  breakpoint: Breakpoint;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isLarge: boolean;
  width: number;
  height: number;
}

export const useBreakpoint = (): BreakpointHook => {
  const [dimensions, setDimensions] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1024,
    height: typeof window !== 'undefined' ? window.innerHeight : 768,
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Handle orientation change on mobile
    window.addEventListener('orientationchange', () => {
      // Small delay to ensure dimensions are updated after orientation change
      setTimeout(handleResize, 100);
    });

    // Initial call
    handleResize();

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  // Determine current breakpoint
  const getBreakpoint = (width: number): Breakpoint => {
    if (width < breakpoints.mobile) return 'mobile';
    if (width < breakpoints.tablet) return 'tablet';
    if (width < breakpoints.desktop) return 'desktop';
    if (width < breakpoints.large) return 'large';
    return 'large';
  };

  const breakpoint = getBreakpoint(dimensions.width);

  return {
    breakpoint,
    isMobile: breakpoint === 'mobile',
    isTablet: breakpoint === 'tablet',
    isDesktop: breakpoint === 'desktop' || breakpoint === 'large',
    isLarge: breakpoint === 'large',
    width: dimensions.width,
    height: dimensions.height,
  };
};

// Utility functions for responsive design
export const useResponsive = () => {
  const { isMobile, isTablet, isDesktop } = useBreakpoint();

  return {
    isMobile,
    isTablet,
    isDesktop,
    isMobileOrTablet: isMobile || isTablet,
    showSidebar: isDesktop, // Sidebar only shows on desktop
    useBottomNav: isMobile || isTablet, // Bottom navigation for mobile/tablet
    containerPadding: isMobile ? '0.5rem' : isTablet ? '1rem' : '2rem',
    headerHeight: isMobile ? '60px' : '70px',
    navHeight: isMobile || isTablet ? '70px' : '0px', // Bottom nav height
  };
};